import type { CostByInitiativeResponse } from '../../../../api/financials-client/financials-client.type';
import { formatCurrency, formatCurrencyCompact } from '../../../../helpers/currency-helpers';

type DataPoint = {
  date: string;
  [key: string]: string | number;
};

/**
 * Filters the cost data based on optional initiative IDs and/or team ID
 *
 * @param {CostByInitiativeResponse[] | undefined} data - The raw cost data from the API
 * @param {string[] | undefined} initiativeIds - Optional array of initiative IDs to include
 * @param {string | undefined} teamId - Optional team ID to filter by
 * @returns {CostByInitiativeResponse[]} Filtered cost data
 */
const filterCostByInitiativeData = (
  data: CostByInitiativeResponse[] | undefined,
  initiativeIds?: string[],
  teamId?: string,
): CostByInitiativeResponse[] => {
  if (!data) return [];

  let filteredData = [...data];

  // Filter by initiative IDs if provided
  if (initiativeIds && initiativeIds.length > 0) {
    filteredData = filteredData.filter((item) => initiativeIds.includes(item.id));
  }

  // Filter by team ID if provided
  if (teamId) {
    filteredData = filteredData
      .map((initiative) => {
        const newInitiative = { ...initiative };
        // Filter the teams array to only include the specified team
        const filteredTeams = initiative.chart_data.teams.filter((team) => team.id === teamId);

        // If after filtering there are no teams left, return null to exclude this initiative
        if (filteredTeams.length === 0) {
          return null;
        }

        // Replace the initiative costs with the specific team's costs
        // This ensures we only return team data, not total data
        newInitiative.chart_data = {
          initiative: filteredTeams[0].costs, // Use the team costs instead of initiative totals
          teams: filteredTeams,
        };

        return newInitiative;
      })
      .filter(Boolean) as CostByInitiativeResponse[];
  }

  return filteredData;
};

/**
 * Transforms the filtered cost data into a format that charts can use
 *
 * @param {CostByInitiativeResponse[]} data The filtered cost data
 * @returns {DataPoint[]} Data formatted for chart
 */
const transformCostByInitiativeData = (data: CostByInitiativeResponse[]): DataPoint[] => {
  // Find all unique dates across all initiatives
  const allDates = new Set<string>();
  data.forEach((initiative) => {
    Object.keys(initiative.chart_data.initiative).forEach((date) => {
      allDates.add(date);
    });
  });

  // Sort dates chronologically
  const sortedDates = Array.from(allDates).sort();
  const line: DataPoint[] = sortedDates.map((date) => {
    const dataPoint: DataPoint = { date };

    // Add data for each initiative
    data.forEach((initiative) => {
      const value = initiative.chart_data.initiative[date] || 0;
      dataPoint[initiative.id] = value;
    });

    return dataPoint;
  });

  return line;
};

/**
 * Helper function to get an initiative name & id map from the data
 *
 * @param {CostByInitiativeResponse[] | undefined} data The raw cost data from the API
 * @returns {Record<string, string>} A map of initiative id to initiative name
 */
function getInitiativeInfo(data: CostByInitiativeResponse[] | undefined): { [id: string]: string } {
  if (!data) return {};
  return data.reduce(
    (acc, initiative) => {
      acc[initiative.id] = initiative.name;
      return acc;
    },
    {} as { [id: string]: string },
  );
}

/**
 * Formats the Y-axis tick value for the cost by initiative chart based on a threshold.
 * Values below 1000 will be formatted as standard currency (e.g., $800.00),
 * while values equal to or above 1000 will use a compact currency format (e.g., $1K).
 *
 * @param {number} value - The numeric value to format for the Y-axis tick.
 * @param {string} currencyCode - The ISO currency code to use for formatting.
 * @returns {string} The formatted tick label as a string.
 */
const formatYAxisTickValue = (value: number, currencyCode: string): string => {
  const CURRENCY_FORMAT_THRESHOLD = 1000;

  if (value < CURRENCY_FORMAT_THRESHOLD) {
    return formatCurrency(value, { currency: currencyCode });
  }

  return formatCurrencyCompact(value, { currency: currencyCode });
};

export { filterCostByInitiativeData, formatYAxisTickValue, getInitiativeInfo, transformCostByInitiativeData };
